import {
  CurtailmentData,
  ReadingsDataProcessedResponse,
  ReadingsDataRawResponse,
} from "@inrange/building-manager-api-client";
import { useMemo } from "react";
import { Col, Row, Table } from "react-bootstrap";
import { Formatters } from "./types";

const SourceTypeCounts = ({
  dataMode,
  processedData,
  rawData,
  curtailmentData,
  hasBattery,
  selectedEsysUuid,
  startDate,
  endDate,
  formatters,
}: {
  dataMode: "processed" | "raw";
  processedData?: ReadingsDataProcessedResponse;
  rawData?: ReadingsDataRawResponse;
  curtailmentData?: CurtailmentData;
  hasBattery: boolean;
  selectedEsysUuid: string | undefined;
  startDate: string;
  endDate: string;
  formatters: Formatters;
}) => {
  // Measurement order matching RecentDataTableProcessed column order
  const measurementOrder = useMemo(() => {
    const order = ["Generation", "Import", "Export"];
    if (hasBattery) order.push("Battery Final Charge");
    order.push("Curtailment", "Est. Curtailed Energy");
    return order;
  }, [hasBattery]);

  // Extract source type counts broken down by measurement
  const sourceTypeCounts = useMemo(() => {
    // Key: "measurement|source" -> { count, earliest, latest }
    const counts = new Map<
      string,
      { measurement: string; source: string; count: number; earliest: string; latest: string }
    >();

    const addCount = (measurement: string, source: string | undefined, timestamp: string) => {
      if (!source) return;
      const key = `${measurement}|${source}`;
      const existing = counts.get(key);
      if (existing) {
        existing.count += 1;
        if (timestamp < existing.earliest) existing.earliest = timestamp;
        if (timestamp > existing.latest) existing.latest = timestamp;
      } else {
        counts.set(key, { measurement, source, count: 1, earliest: timestamp, latest: timestamp });
      }
    };

    // Pre-calculate timestamp bounds for efficient filtering
    const { startMs, endMs } =
      startDate && endDate
        ? formatters.dateRangeToTimestamps(startDate, endDate)
        : { startMs: 0, endMs: Infinity };

    if (dataMode === "processed" && processedData) {
      processedData.data.forEach((point) => {
        if (selectedEsysUuid && point.esysUuid !== selectedEsysUuid) return;
        if (startDate && endDate) {
          const pointMs = new Date(point.x).getTime();
          if (pointMs < startMs || pointMs > endMs) return;
        }

        addCount("Generation", point.generationSource, point.x);
        addCount("Import", point.importSource, point.x);
        addCount("Export", point.exportSource, point.x);
        if (hasBattery) {
          addCount("Battery Final Charge", point.batteryFinalChargeSource, point.x);
        }
      });

      // Add curtailment source counts
      if (curtailmentData) {
        for (let i = 0; i < curtailmentData.x.length; i++) {
          const ts = curtailmentData.x[i];
          if (startDate && endDate) {
            const pointMs = new Date(ts).getTime();
            if (pointMs < startMs || pointMs > endMs) continue;
          }
          if (curtailmentData.isCurtailed[i] && curtailmentData.isCurtailedSource[i]) {
            addCount("Curtailment", curtailmentData.isCurtailedSource[i], ts);
          }
          if (curtailmentData.estimatedCurtailedKwh[i] != null && curtailmentData.estimatedCurtailedKwhSource[i]) {
            addCount("Est. Curtailed Energy", curtailmentData.estimatedCurtailedKwhSource[i], ts);
          }
        }
      }
    } else if (dataMode === "raw" && rawData) {
      rawData.series.forEach((s) => {
        if (selectedEsysUuid && s.esysUuid !== selectedEsysUuid) return;
        s.data.x.forEach((timestamp, index) => {
          if (startDate && endDate) {
            const pointMs = new Date(timestamp).getTime();
            if (pointMs < startMs || pointMs > endMs) return;
          }
          addCount("Raw", s.data.source[index], timestamp);
        });
      });
    }

    // Sort by measurement order, then by count descending
    return Array.from(counts.values()).sort((a, b) => {
      const orderA = measurementOrder.indexOf(a.measurement);
      const orderB = measurementOrder.indexOf(b.measurement);
      if (orderA !== orderB) return orderA - orderB;
      return b.count - a.count;
    });
  }, [
    dataMode,
    processedData,
    rawData,
    curtailmentData,
    selectedEsysUuid,
    startDate,
    endDate,
    hasBattery,
    measurementOrder,
    formatters,
  ]);

  return (
    <>
      {sourceTypeCounts.length > 0 && (
        <Row className="mt-4">
          <Col>
            <h5>Source Types</h5>
            <p className="text-muted">
              Showing {sourceTypeCounts.length} unique source type
              {sourceTypeCounts.length !== 1 ? "s" : ""} in the selected time
              period
            </p>
            <div style={{ maxHeight: "400px", overflowY: "auto" }}>
              <Table striped bordered hover size="sm" responsive>
                <thead
                  style={{
                    position: "sticky",
                    top: 0,
                    background: "white",
                  }}
                >
                  <tr>
                    <th>Measurement</th>
                    <th>Source Type</th>
                    <th>Count</th>
                    <th>Earliest</th>
                    <th>Latest</th>
                  </tr>
                </thead>
                <tbody>
                  {sourceTypeCounts.map((item, index) => (
                    <tr key={index}>
                      <td>{item.measurement}</td>
                      <td>
                        <code>{item.source}</code>
                      </td>
                      <td>{item.count.toLocaleString()}</td>
                      <td>{formatters.toLocaleString(item.earliest)}</td>
                      <td>{formatters.toLocaleString(item.latest)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Col>
        </Row>
      )}
    </>
  );
};

export default SourceTypeCounts;
